import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Trans } from '@lingui/macro'
import cn from '@meltdownjs/cn'
import colors from 'tailwindcss/colors'

import {
  ArrowRightIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

import useCart from 'src/hooks/data/useCart'
import useOrderBudget from 'src/hooks/data/useOrderBudget'

import useDefaultCompanyUserPermissions from 'src/hooks/utils/useDefaultCompanyUserPermissions'

import Currency from 'src/components/Currency'
import {
  FloatingArrow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/Tooltip'

const CheckoutButton = ({
  companyUserReference,
}: {
  companyUserReference: string
}) => {
  const { cartId, companyId } = useParams()
  const cartQuery = useCart(cartId, {
    notifyOnChangeProps: 'tracked',
    select: ({
      items,
      totals,
      currency,
      validationMessages,
      deliveryDates,
    }) => ({
      totals,
      currency,
      itemsWithMessages: Object.values(items).filter(
        ({ messages }) => messages?.length > 0
      ),
      totalItems: Object.values(items).reduce(
        (sum, { quantity }) => sum + quantity,
        0
      ),
      hasMessages: validationMessages.length > 0,
      hasItemsWithMessages: Object.values(items).some(
        ({ messages }) => messages?.length > 0
      ),
      deliveryDates: deliveryDates,
    }),
  })

  const { ability } = useDefaultCompanyUserPermissions()
  const { data: orderBudget, isSuccess } = useOrderBudget(
    companyUserReference!,
    {
      enabled: !!companyUserReference,
    }
  )

  const cart = cartQuery?.data
  const hasMultipleDeliveryDates = cart?.deliveryDates.length > 1

  const hasOrderBudget =
    isSuccess &&
    !!orderBudget &&
    cart?.companyUserReference === companyUserReference &&
    ability.can('see', 'orderBudget')

  const availableBudget = hasOrderBudget
    ? orderBudget?.budget - cart.totals.subtotal
    : 0

  const hasNegativeBudget = availableBudget < 0

  const [isOpen, setIsOpen] = useState(true)

  const tooltipTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsOpen(!isOpen)
  }

  const tooltipClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsOpen(false)
  }

  if (!cart || cart.totalItems <= 0) {
    return null
  }

  return (
    <Link
      to={`/${companyId}/carts/${cartId}/checkout`}
      state={{ from: 'grid' }}
    >
      <div
        className={cn(
          'flex h-16 rounded-md bg-green-400 text-gray-900 shadow-lg drop-shadow-lg',
          {
            'bg-red-600 text-gray-50': hasNegativeBudget,
          }
        )}
      >
        {hasNegativeBudget && (
          <Tooltip open={hasNegativeBudget}>
            <TooltipTrigger onClick={tooltipTriggerClick}>
              <div className="flex h-full items-center justify-center rounded-l-md bg-red-200 p-3 text-red-700">
                <span className="h-8 w-8">
                  <ExclamationCircleIcon />
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent onClick={tooltipClose}>
              <div>
                <button
                  className="absolute right-1.5 top-0 z-10 m-2 flex h-10 items-center justify-center md:h-8"
                  onClick={tooltipClose}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="py- rounded-md bg-red-600 px-4 text-gray-50 shadow-lg drop-shadow-lg">
                <Trans>
                  Weekly budget exceeded by{' '}
                  <Currency cents={availableBudget} currencyIsoCode={'EUR'} />
                </Trans>
              </div>
              <FloatingArrow fill={colors.red[600]} />
            </TooltipContent>
          </Tooltip>
        )}

        {hasMultipleDeliveryDates && !hasNegativeBudget && (
          <Tooltip open={isOpen}>
            <TooltipTrigger onClick={tooltipTriggerClick}>
              <div className="flex h-full items-center justify-center rounded-l-md bg-green-100 p-3 text-green-800">
                <span className="h-8 w-8">
                  <ExclamationTriangleIcon />
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent onClick={tooltipClose}>
              <div>
                <button
                  className="absolute right-1.5 top-0 z-10 m-2 flex h-10 items-center justify-center md:h-8"
                  onClick={tooltipClose}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="flex space-x-2 rounded-md bg-green-100 px-4 py-3 text-gray-900 shadow-lg drop-shadow-lg">
                <div>
                  <ExclamationTriangleIcon className="h-8 w-8 text-green-800" />
                </div>
                <div className="max-w-[220px]">
                  <Trans>
                    This shopping cart has several delivery dates. You can edit
                    these in the checkout.
                  </Trans>
                </div>
              </div>
              <FloatingArrow fill={colors.green[100]} />
            </TooltipContent>
          </Tooltip>
        )}
        <div
          className={cn('flex flex-col items-center justify-center px-6 py-4', {
            'space-x-3': hasOrderBudget,
          })}
        >
          <div className="flex h-10 items-center space-x-2">
            <span className="text-base font-semibold">
              <Trans>To checkout</Trans>
            </span>
            <span className="w-6">
              <ArrowRightIcon />
            </span>
          </div>
          {hasOrderBudget && (
            <Currency
              cents={availableBudget}
              currencyIsoCode={'EUR'}
              className="text-xs font-semibold"
            />
          )}
        </div>
      </div>
    </Link>
  )
}

export default CheckoutButton
